import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import {
  LayoutDashboard, Plus, History, Wallet, Target, ReceiptText,
  LineChart, FileBarChart, BookOpen, Sparkles, Moon, Sun, LogOut,
  Menu, X, ShieldCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/add", label: "Add Transaction", icon: Plus, testid: "nav-add-transaction" },
  { to: "/history", label: "History", icon: History, testid: "nav-history" },
  { to: "/accounts", label: "Accounts", icon: Wallet, testid: "nav-accounts" },
  { to: "/goals", label: "Goals", icon: Target, testid: "nav-goals" },
  { to: "/billing", label: "Billing", icon: ReceiptText, testid: "nav-billing" },
  { to: "/portfolio", label: "Portfolio", icon: LineChart, testid: "nav-portfolio" },
  { to: "/reports", label: "Reports", icon: FileBarChart, testid: "nav-reports" },
  { to: "/insight", label: "AI Insight", icon: Sparkles, testid: "nav-ai-insight" },
  { to: "/guide", label: "Guide", icon: BookOpen, testid: "nav-guide" },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const [lockDialogOpen, setLockDialogOpen] = useState(false);
  const [lockedFeatureName, setLockedFeatureName] = useState("");
  const [requiredTier, setRequiredTier] = useState("");

  const isPathLocked = (to) => {
    if (user?.role === 'admin') return false;
    const tier = user?.subscription_tier || '1_month';
    if (tier === '1_month') {
      return ["/portfolio", "/billing", "/insight"].includes(to);
    }
    if (tier === '6_months') {
      return ["/insight"].includes(to);
    }
    return false;
  };

  const handleNavItemClick = (e, item) => {
    if (isPathLocked(item.to)) {
      e.preventDefault();
      setLockedFeatureName(item.label);
      setRequiredTier(item.to === "/insight" ? "1 Tahun (1 Year)" : "6 Bulan (6 Months) / 1 Tahun (1 Year)");
      setLockDialogOpen(true);
    } else {
      setMobileOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-card border-r border-border transform transition-transform ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        <div className="h-16 flex items-center px-6 border-b border-border">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="brand-link">
            <img src="/logo.png" alt="Aliran Logo" className="h-7 w-7 object-contain" />
            <span className="font-heading text-lg font-medium tracking-tight">Aliran</span>
          </Link>
        </div>
        <nav className="p-3 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              onClick={(e) => handleNavItemClick(e, item)}
              className={({ isActive }) =>
                `flex items-center justify-between px-3 py-2 text-sm rounded transition-colors ${
                  isActive && !isPathLocked(item.to)
                    ? "bg-foreground text-background font-medium"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent"
                } ${isPathLocked(item.to) ? "opacity-60 hover:opacity-100" : ""}`
              }
            >
              <div className="flex items-center gap-3">
                <item.icon className="h-4 w-4" />
                <span>{item.label}</span>
              </div>
              {isPathLocked(item.to) && (
                <span className="text-[10px] bg-warning/10 text-warning px-1.5 py-0.5 rounded border border-warning/20 font-medium">
                  Locked
                </span>
              )}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 text-sm rounded transition-colors ${
                  isActive
                    ? "bg-foreground text-background font-medium"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent"
                }`
              }
            >
              <ShieldCheck className="h-4 w-4" />
              <span>Admin</span>
            </NavLink>
          )}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-border">
          <div className="flex items-center gap-3 px-3 py-2">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="h-8 w-8 rounded-full" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center text-xs font-medium">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="text-xs text-muted-foreground truncate">{user?.role}</div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="sticky top-0 z-30 h-16 border-b border-border bg-background/80 backdrop-blur-xl flex items-center justify-between px-4 md:px-8">
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileOpen((s) => !s)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <div className="hidden md:block">
            <p className="label-caps">Personal Finance</p>
            <p className="font-heading text-lg tracking-tight">Welcome back, {user?.name?.split(" ")[0]}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggle}
              data-testid="theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={logout}
              data-testid="logout-btn"
              aria-label="Logout"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-4 md:p-8 max-w-[1600px] w-full">
          <Outlet />
        </main>
      </div>

      <Dialog open={lockDialogOpen} onOpenChange={setLockDialogOpen}>
        <DialogContent className="sm:max-w-md bg-card/95 border border-border backdrop-blur-xl rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-medium flex items-center gap-2 text-warning">
              🔒 Fitur Terkunci
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground mt-2">
              Fitur <strong className="text-foreground">{lockedFeatureName}</strong> hanya tersedia untuk pelanggan paket subscription <strong className="text-foreground">{requiredTier}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 text-sm text-foreground/80 leading-relaxed">
            Paket subscription Anda saat ini tidak mencakup akses ke halaman ini. Silakan hubungi admin untuk melakukan upgrade langganan Anda.
          </div>
          <DialogFooter className="flex justify-end gap-2 mt-4">
            <Button onClick={() => setLockDialogOpen(false)} className="bg-foreground text-background hover:bg-foreground/90">
              Mengerti
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
